import type { CSSProperties } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import { Arrows } from '../components/atomic/buttons/arrows/Arrows';
import { GoBackButton } from '../components/atomic/buttons/go_back_button/GoBackButton';
import { SunBeams } from '../components/atomic/atmosphere/sun_beams/SunBeams';
import { InfoPopupButton } from '../components/atomic/buttons/info_popup_button/InfoPopupButton';
import { InfoPopupFrame2 } from '../components/atomic/dialog/info_popup_frame_2/InfoPopupFrame2';
import { ScreenHeader } from '../components/atomic/layout/screen_header/ScreenHeader';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import bodyTextSvg from './info_popup/body_text.svg';
import './info_popup/PlayerLevelInfoPopup.css';
import titleTextSvg from './info_popup/title_text.svg';
import { LevelCards } from './level_cards/LevelCards';
import {
  LEVEL_ARROW_POSITIONS,
  PLAYER_LEVEL_ARROW_SCALE,
  MAX_LEVEL_PAGE,
  type PlayerLevelScreenModel,
} from './PlayerLevelScreenModelScreen';
import { HeaderText } from './texts/HeaderText';
import type { PlayerLevelScreenController } from './PlayerLevelScreenController';
import './player_level_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useSwipeNavigate } from '../shared/gestures/useSwipeNavigate';

export type PlayerLevelScreenViewProps = ScreenViewProps<
  PlayerLevelScreenModel,
  PlayerLevelScreenController
>;

/** Courier license screen (Figma concept 161:17007). */
export function PlayerLevelScreenView({
  model,
  controller,
}: PlayerLevelScreenViewProps) {
  const swipe = useSwipeNavigate({
    axis: 'vertical',
    onSwipe: controller.handleNavigate,
    enabled: !model.infoOpen,
  });
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);

  return (
    <div
      className="app-screen player-level-screen"
      style={swipe.style}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClickCapture={swipe.onClickCapture}
    >
      <SkyAtmosphere variant="welcome" />
      <SunBeams className="player-level-screen__sun-beams" />
      <div
        className="player-level-plaques"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <ScreenHeader showSubHeader={false} />
        <HeaderText />
        <LevelCards
          page={model.page}
          successfulDeliveries={model.successfulDeliveries}
          averageRating={model.averageRating}
        />
        <Arrows
          directions={['up', 'down']}
          positions={LEVEL_ARROW_POSITIONS}
          scale={PLAYER_LEVEL_ARROW_SCALE}
          canNavigate={{
            up: model.page > 0,
            down: model.page < MAX_LEVEL_PAGE,
          }}
          onNavigate={controller.handleNavigate}
        />
        <InfoPopupButton
          className={model.infoOpen ? 'info-popup-button--above-popup' : undefined}
          left={919}
          top={157}
          scale={1.503672}
          onClick={controller.handleInfoButtonClick}
        />
        {model.infoOpen ? (
          <InfoPopupFrame2
            titleSrc={titleTextSvg}
            bodySrc={bodyTextSvg}
            titleClassName="player-level-info-popup__title"
            bodyClassName="player-level-info-popup__body"
            titleAlt="This is your personal courier license!"
            bodyAlt="The more successful deliveries and the better the rating, the higher your status at the delivery school."
            onClose={controller.handleInfoClose}
          />
        ) : null}
        <GoBackButton onClick={controller.handleGoBack} />
      </div>
    </div>
  );
}
